import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getSignupRequest,
  approveSignupRequest,
  rejectSignupRequest,
  listSignupRequests,
} from '../firebase/admin';

const name = (r) => r.displayName || r.email || r.uid;

/* Single-request view, reached from the email links:
   /admin/approve?req=<id>&action=approve|reject */
export const ApprovalView = ({ requestId }) => {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setReq(await getSignupRequest(requestId));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (requestId) load();
    else {
      setLoading(false);
      setError('Missing request id.');
    }
  }, [requestId, load]);

  const act = async (action) => {
    setBusy(action);
    setError('');
    try {
      if (action === 'approve') await approveSignupRequest(requestId);
      else await rejectSignupRequest(requestId);
      setDone(action);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  let body;
  if (loading) body = <p className="ua-muted">Loading…</p>;
  else if (error && !req) body = <p className="admin-error">{error}</p>;
  else if (done)
    body = (
      <>
        <p className="approval-done">
          {done === 'approve'
            ? '✓ Approved — member access granted.'
            : '✕ Rejected — the account was disabled.'}
        </p>
        <Link className="btn-primary" to="/admin">Back to dashboard</Link>
      </>
    );
  else if (req && req.status !== 'pending')
    body = (
      <>
        <p>
          Already <strong>{req.status}</strong>
          {req.actionedBy ? ` by ${req.actionedBy}` : ''}.
        </p>
        <Link className="btn-primary" to="/admin">Back to dashboard</Link>
      </>
    );
  else if (req)
    body = (
      <>
        <div className="approval-req">
          <strong>{name(req)}</strong>
          {req.email && req.email !== name(req) && <span>{req.email}</span>}
          {req.providers && <span className="approval-sub">via {req.providers}</span>}
        </div>
        <p className="approval-prompt">Grant this person member access to the site?</p>
        <div className="approval-actions">
          <button className="btn-primary" disabled={!!busy} onClick={() => act('approve')}>
            {busy === 'approve' ? 'Approving…' : 'Approve'}
          </button>
          <button className="approval-reject" disabled={!!busy} onClick={() => act('reject')}>
            {busy === 'reject' ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
        {error && <p className="admin-error">{error}</p>}
      </>
    );

  return (
    <div className="admin-shell">
      <div className="admin-center glass approval-card">
        <img src="/images/logo.png" alt="Dutchess County Bhabani" className="approval-logo" />
        <h1>Member request</h1>
        {body}
      </div>
    </div>
  );
};

/* The "Approvals" tab: list of pending requests. */
export const RequestsList = () => {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setReqs(await listSignupRequests('pending'));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    setBusy(id + action);
    setError('');
    try {
      if (action === 'approve') await approveSignupRequest(id);
      else await rejectSignupRequest(id);
      setReqs((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="requests glass">
      <div className="requests-head">
        <h2>
          Pending Requests {!loading && <span className="ua-count">{reqs.length}</span>}
        </h2>
        <button className="ua-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : '↻'}
        </button>
      </div>
      {loading ? (
        <p className="ua-muted">Loading…</p>
      ) : reqs.length === 0 ? (
        <p className="ua-muted">No pending requests.</p>
      ) : (
        <ul className="requests-list">
          {reqs.map((r) => (
            <li key={r.id} className="request-row">
              <div className="request-info">
                <span className="request-name">{name(r)}</span>
                <span className="request-sub">
                  {r.email || r.uid}
                  {r.providers ? ` · ${r.providers}` : ''}
                </span>
              </div>
              <div className="request-actions">
                <button className="btn-primary" disabled={!!busy} onClick={() => act(r.id, 'approve')}>
                  {busy === r.id + 'approve' ? '…' : 'Approve'}
                </button>
                <button className="approval-reject" disabled={!!busy} onClick={() => act(r.id, 'reject')}>
                  {busy === r.id + 'reject' ? '…' : 'Reject'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="ua-error">{error}</p>}
    </div>
  );
};
